/**
 * Hugin's setting cards in the setup (#154 HUG-05, HUG-06): the opening hours as a seven-day form, not the
 * field's JSON; each card's values edited before Utfør (PUT /poc/actions/:id, the server keeps the guard the
 * card was proposed with); a save refused because the setting changed since keeps the typed values; and a
 * guarded Undo right after a save, which restores what was there only while nothing changed since (the
 * company row's `expected`, the staffing need's revision: the server refuses otherwise).
 */
import { ApiError, errorText, get, put } from '@/api/client';
import { T } from '@/i18n';
import Apningstider from '@/pages/innstillinger/Apningstider';
import type { OpeningHours } from '@/shell/tenant';
import { useState } from 'react';
import { create } from 'zustand';

/** What the server sends with an editable card (services/poc/proposalEdit.js editOf). */
export interface SettingEdit {
  route: string;
  path: string;
  body: Record<string, unknown>;
  previous: Record<string, unknown> | null;
}
type Values = Record<string, unknown>;
const GUARDS = ['expected', 'revision', 'expected_revision', 'if_unchanged', 'expected_skills'];
export const valuesOf = (body: Values): Values =>
  Object.fromEntries(Object.entries(body).filter(([k]) => !GUARDS.includes(k)));
const pick = (v: Values, keys: string[]) => Object.fromEntries(keys.map((k) => [k, v[k] ?? null]));

/* ── the staffing need as one block a day: from, to, how many ─────────────── */
type Need = Record<string, Record<string, number>>;
type Block = { from: number; to: number; n: number } | null;
const DAYS = ['Mandag', 'Tirsdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lørdag', 'Søndag'];
/** each day as one run of the same count, or undefined when a day has more than one (then it is not edited here) */
export function blocksOf(need: unknown): Block[] | undefined {
  const n = (need && typeof need === 'object' ? need : {}) as Need;
  const out: Block[] = [];
  for (let d = 0; d < 7; d++) {
    const hours = Object.entries(n[String(d)] ?? {})
      .map(([h, c]) => [Number(h), Number(c)] as const)
      .filter(([, c]) => c > 0)
      .sort((a, b) => a[0] - b[0]);
    const first = hours[0];
    const last = hours[hours.length - 1];
    if (!first || !last) {
      out.push(null);
      continue;
    }
    const block = { from: first[0], to: last[0] + 1, n: first[1] };
    if (hours.length !== block.to - block.from || hours.some(([, c]) => c !== block.n)) return undefined;
    out.push(block);
  }
  return out;
}
export function needOf(blocks: Block[]): Need {
  const need: Need = {};
  blocks.forEach((b, d) => {
    if (!b || b.n <= 0 || b.to <= b.from) return;
    const day: Record<string, number> = {};
    for (let h = b.from; h < b.to; h++) day[String(h)] = b.n;
    need[String(d)] = day;
  });
  return need;
}

function StaffingForm({ value, onChange }: { value: unknown; onChange: (v: Need) => void }) {
  const blocks = blocksOf(value) ?? [];
  const set = (d: number, b: Block) => onChange(needOf(blocks.map((x, i) => (i === d ? b : x))));
  // a block starts on the hour 00 to 23 and ends on 01 to 24
  const hour = (label: string, v: number, first: number, on: (h: number) => void) => (
    <select className="inp" aria-label={label} value={v} onChange={(e) => on(Number(e.target.value))}>
      {Array.from({ length: 24 }, (_, i) => i + first).map((h) => (
        <option key={h} value={h}>
          {String(h).padStart(2, '0')}
        </option>
      ))}
    </select>
  );
  return (
    <div className="pc-edit-need" data-testid="setting-edit-need">
      {DAYS.map((name, d) => {
        const b = blocks[d] ?? null;
        return (
          <div key={name} className="pc-edit-row">
            <span>{T(name)}</span>
            {b ? (
              <>
                {hour(`${T(name)}, ${T('fra')}`, b.from, 0, (h) => set(d, { ...b, from: h }))}
                <span aria-hidden>–</span>
                {hour(`${T(name)}, ${T('til')}`, b.to, 1, (h) => set(d, { ...b, to: h }))}
                <input
                  className="inp"
                  type="number"
                  min={1}
                  max={999}
                  aria-label={`${T(name)}, ${T('antall på jobb')}`}
                  value={b.n}
                  onChange={(e) => set(d, { ...b, n: Math.max(1, Math.round(Number(e.target.value) || 1)) })}
                />
                <button type="button" className="pc-ghost" onClick={() => set(d, null)}>
                  {T('Fjern')}
                </button>
              </>
            ) : (
              <>
                <span className="hint">{T('Ingen')}</span>
                <button type="button" className="pc-ghost" onClick={() => set(d, { from: 10, to: 22, n: 1 })}>
                  {T('Legg til')}
                </button>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Whether every value on the card has a form here; a staffing need of several runs a day is not edited here. */
export function canEdit(edit: SettingEdit | undefined): edit is SettingEdit {
  if (!edit) return false;
  return Object.entries(valuesOf(edit.body)).every(([k, v]) =>
    k === 'opening_hours'
      ? v === null || typeof v === 'object'
      : k === 'staffing_need'
        ? blocksOf(v) !== undefined
        : v === null || ['string', 'number', 'boolean'].includes(typeof v),
  );
}

const TIME = /^\d{2}:\d{2}(:\d{2})?$/;
const LABEL: Record<string, string> = { name: 'Navn', start_time: 'Start', end_time: 'Slutt' };

/** The card's values as the settings show them, each one editable. */
export function SettingForm({ values, onChange }: { values: Values; onChange: (v: Values) => void }) {
  const set = (k: string, v: unknown) => onChange({ ...values, [k]: v });
  return (
    <div className="pc-edit" data-testid="setting-edit">
      {Object.entries(values).map(([k, v]) =>
        k === 'opening_hours' ? (
          <Apningstider key={k} hours={v as OpeningHours | null} onSave={async (next) => set(k, next)} />
        ) : k === 'staffing_need' ? (
          <StaffingForm key={k} value={v} onChange={(next) => set(k, next)} />
        ) : (
          <div key={k} className="pc-edit-row">
            <span>{T(LABEL[k] ?? k)}</span>
            {typeof v === 'boolean' ? (
              <input
                type="checkbox"
                aria-label={T(LABEL[k] ?? k)}
                checked={v}
                onChange={(e) => set(k, e.target.checked)}
              />
            ) : (
              <input
                className="inp"
                aria-label={T(LABEL[k] ?? k)}
                type={
                  typeof v === 'number' ? 'number' : typeof v === 'string' && TIME.test(v) ? 'time' : 'text'
                }
                value={v == null ? '' : String(v)}
                onChange={(e) =>
                  set(k, typeof v === 'number' ? Number(e.target.value) : e.target.value || null)
                }
              />
            )}
          </div>
        ),
      )}
    </div>
  );
}

/* ── after Utfør: the Undo, and the values a refused save kept ─────────────── */
/** Settings whose save can be undone here: the company row (by `expected`) and the staffing need (by revision). */
const UNDOABLE = ['PUT /tenant/:id', 'PUT /admin/labour-settings'];
export interface Saved {
  id: string;
  title: string;
  edit: SettingEdit;
  answer: unknown;
}
export interface Kept {
  id: string;
  title: string;
  edit: SettingEdit;
  message: string;
}
export const useSettingSaves = create<{
  saved: Saved[];
  kept: Kept[];
  add: (x: { saved?: Saved; kept?: Kept }) => void;
  drop: (id: string) => void;
}>((set) => ({
  saved: [],
  kept: [],
  add: ({ saved, kept }) =>
    set((s) => ({
      saved: saved ? [...s.saved.filter((x) => x.id !== saved.id), saved] : s.saved,
      kept: kept ? [...s.kept.filter((x) => x.id !== kept.id), kept] : s.kept,
    })),
  drop: (id) =>
    set((s) => ({ saved: s.saved.filter((x) => x.id !== id), kept: s.kept.filter((x) => x.id !== id) })),
}));

/** After Utfør on an editable card: an Undo for a save, the typed values for a save refused as changed since. */
export function noteOutcome(
  card: { action_id: string; title: string; edit?: SettingEdit | undefined },
  r: unknown,
  e?: unknown,
) {
  const edit = card.edit;
  if (!edit) return;
  if (!e) {
    const answer = (r as { applied?: boolean; answer?: unknown } | undefined) ?? {};
    const revision = (answer.answer as { revision?: unknown } | undefined)?.revision;
    if (
      answer.applied &&
      edit.previous &&
      UNDOABLE.includes(edit.route) &&
      (edit.route !== 'PUT /admin/labour-settings' || Number.isInteger(revision))
    )
      useSettingSaves
        .getState()
        .add({ saved: { id: card.action_id, title: card.title, edit, answer: answer.answer } });
    return;
  }
  const codes = e instanceof ApiError ? ((e.body as { codes?: string[] } | null)?.codes ?? []) : [];
  if (codes.includes('setting_changed') || codes.includes('revision_conflict'))
    useSettingSaves.getState().add({
      kept: {
        id: card.action_id,
        title: card.title,
        edit,
        message: T(
          'Innstillingen ble endret etter at forslaget ble laget, så ingenting er lagret. Verdiene dine står her.',
        ),
      },
    });
}

const refresh = () => window.dispatchEvent(new Event('timefly:ai-pending'));

function UndoRow({ s, onUndone }: { s: Saved; onUndone?: () => void }) {
  const drop = useSettingSaves((x) => x.drop);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const undo = async () => {
    setBusy(true);
    const prev = s.edit.previous ?? {};
    const body =
      s.edit.route === 'PUT /tenant/:id'
        ? { ...prev, expected: pick(valuesOf(s.edit.body), Object.keys(prev)) }
        : { ...prev, revision: (s.answer as { revision: number }).revision };
    try {
      await put(s.edit.path, body);
      setSaid(T('Angret. Det som var lagret før, er tilbake.'));
      onUndone?.();
      refresh();
    } catch (e) {
      setSaid(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="pc-card pc-card-undo" data-testid="setting-undo">
      <div className="pc-card-what">{T('Lagret: {what}', { what: s.title })}</div>
      {said && <output className="pc-card-note">{said}</output>}
      <div className="pc-card-buttons">
        {!said && (
          <button type="button" className="pc-ghost" disabled={busy} onClick={() => void undo()}>
            {T('Angre')}
          </button>
        )}
        <button type="button" className="pc-ghost" disabled={busy} onClick={() => drop(s.id)}>
          {T('Lukk')}
        </button>
      </div>
    </div>
  );
}

function KeptRow({ k }: { k: Kept }) {
  const drop = useSettingSaves((x) => x.drop);
  const [values, setValues] = useState<Values>(() => valuesOf(k.edit.body));
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  // saved over what is there now, read now: the person was told it changed and chose their values
  const save = async () => {
    setBusy(true);
    try {
      if (k.edit.route === 'PUT /tenant/:id') {
        const now = await get<Values>(k.edit.path.replace(/^\/tenant\//, '/tenants/'));
        await put(k.edit.path, { ...values, expected: pick(now, Object.keys(values)) });
      } else if (k.edit.route === 'PUT /admin/labour-settings') {
        const now = await get<{ revision: number }>('/admin/labour-settings');
        await put(k.edit.path, { ...values, revision: now.revision });
      } else {
        await put(k.edit.path, values);
      }
      setSaid(T('Lagret.'));
      refresh();
    } catch (e) {
      setSaid(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="pc-card pc-card-kept" data-testid="setting-kept">
      <div className="pc-card-what">{k.title}</div>
      <p className="pc-error">{k.message}</p>
      {canEdit({ ...k.edit, body: values }) && <SettingForm values={values} onChange={setValues} />}
      {said && <output className="pc-card-note">{said}</output>}
      <div className="pc-card-buttons">
        {said !== T('Lagret.') && (
          <button type="button" className="pc-send" disabled={busy} onClick={() => void save()}>
            {T('Lagre likevel')}
          </button>
        )}
        <button type="button" className="pc-ghost" disabled={busy} onClick={() => drop(k.id)}>
          {T('Forkast')}
        </button>
      </div>
    </div>
  );
}

/** Right after a save, beside the setup's receipt: the Undo for each setting saved from a card. */
export function UndoList({ onUndone }: { onUndone?: () => void }) {
  const saved = useSettingSaves((x) => x.saved);
  return (
    <>
      {saved.map((s) => (
        <UndoRow key={s.id} s={s} {...(onUndone ? { onUndone } : {})} />
      ))}
    </>
  );
}

/** Under the waiting cards in the setup's Forslag pane: the values a save refused as changed since kept. */
export function KeptList() {
  const kept = useSettingSaves((x) => x.kept);
  return (
    <>
      {kept.map((k) => (
        <KeptRow key={k.id} k={k} />
      ))}
    </>
  );
}
